import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import QRCode from 'react-qr-code';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faDownload, faEye, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const AttendanceCaptureQR = () => {
  const authState = useSelector(state => state.authReducer);
  const userId = authState.user._id;

  const [fetchData, { loading }] = useFetch();
  const [qrCardData, setQRCardData] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState(new Set());

  const fetchChronoQR = useCallback(() => {
    const config = {
      url: "/attendance/myqrcardlist",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response?.qrCardList) setQRCardData(response.qrCardList);
      })
      .catch(error => console.error("Error fetching QR cards:", error));
  }, [authState.token, fetchData, userId]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchChronoQR();
  }, [authState.isLoggedIn, fetchChronoQR]);

  // Filtered Data
  const filteredCards = useMemo(() => {
    return qrCardData.filter(card =>
      card.qrLocationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.qrCardEntityCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [qrCardData, searchTerm]);

  const toggleExpand = (id) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const origin = window.location.origin;

  const handleDownloadQR = (qrCardCode, qrCardEntityCode, qrLocationName) => {
    const link = document.createElement('a');
    link.href = `${origin}/attendance-qr/${qrCardCode}`;
    link.download = `${qrCardEntityCode}-${qrLocationName}-QR.png`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-gray-100">
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-yellow-500/30 px-4 py-5">
        <h1 className="text-2xl font-bold text-yellow-200 text-center">My Attendance QR Cards</h1>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-8">
          <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search location or entity code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl pl-12 py-4 focus:border-yellow-400"
          />
        </div>

        {loading ? <Loader /> : filteredCards.length === 0 ? (
          <div className="text-center py-20 text-yellow-300 text-xl">
            No QR cards found
          </div>
        ) : (
          <div className="space-y-6">
            {filteredCards.map((card, index) => (
              <div key={card._id} className="bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden">
                <div 
                  className="flex justify-between items-center px-6 py-5 cursor-pointer hover:bg-zinc-800"
                  onClick={() => toggleExpand(card._id)}
                >
                  <div>
                    <span className="text-sm text-gray-400">#{index + 1}</span>
                    <span className="ml-3 font-medium text-white">{card.qrLocationName}</span>
                  </div>
                  <FontAwesomeIcon 
                    icon={expandedCards.has(card._id) ? faChevronUp : faChevronDown} 
                    className="text-yellow-400" 
                  />
                </div>

                {expandedCards.has(card._id) && (
                  <div className="px-6 pb-8">
                    <div className="bg-zinc-800 rounded-2xl p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <p><span className="text-gray-400">Entity Code:</span> {card.qrCardEntityCode}</p>
                        <p><span className="text-gray-400">Owner:</span> {card.qrOwnerName}</p>
                        <p><span className="text-gray-400">Lat:</span> {card.qrCardLat}</p>
                        <p><span className="text-gray-400">Long:</span> {card.qrCardLong}</p>
                      </div>

                      <div className="flex justify-center bg-black rounded-2xl p-6">
                        <QRCode 
                          value={`${origin}/attendance-qr/${card.qrCardCode}`} 
                          size={200} 
                          className="bg-white p-4 rounded-xl" 
                        />
                      </div>

                      <button 
                        onClick={() => handleDownloadQR(card.qrCardCode, card.qrCardEntityCode, card.qrLocationName)}
                        className="w-fit px-2 py-1 flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 rounded-2xl font-medium"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Download QR Code
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceCaptureQR;